import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { axiosInstance } from "../../../utils/networking";
import { formatRelative } from "../ActivityTimeline/timelineUtils";
import { ACCENT, FAINT, INK_2, MONO, PANEL_BG, RULE } from "./styleTokens";

export interface CaseSummaryPayload {
  summary: string | null;
  generated_at: string | null;
  activity_count?: number;
  source?: "cache" | "generated" | "none";
  error?: "not_configured" | "unavailable";
}

interface CaseSummaryCardProps {
  caseId: string;
  /** Refetch whenever the case gains or loses an activity — the server
   *  regenerates when its own activity count has moved on. */
  activityCount: number;
  /** Left half of the panel footer: last touch / touches / staleness chip. */
  footer?: React.ReactNode;
}

const linkSx = {
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  font: "inherit",
  fontWeight: 600,
  color: ACCENT,
  "&:hover": { textDecoration: "underline" },
};

const footerSx = {
  marginTop: "8px",
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap" as const,
  gap: "6px",
  fontSize: "11.5px",
  color: FAINT,
};

/**
 * The generated case summary, drawn as the SUMMARY panel that sits on top of
 * the history feed: a filled grey box with a monospace eyebrow, 1–3 sentences
 * of body text and one tiny muted footer line.
 *
 *  - `source: "none"` / `error: "not_configured"` with no cached text → the
 *    panel keeps its eyebrow and footer so the card top never disappears.
 *  - `error: "unavailable"` with no text → one muted retry line.
 */
const CaseSummaryCard = ({
  caseId,
  activityCount,
  footer,
}: CaseSummaryCardProps) => {
  const [data, setData] = useState<CaseSummaryPayload | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [regenerating, setRegenerating] = useState<boolean>(false);
  const alive = useRef<boolean>(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(() => {
    if (!caseId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    axiosInstance
      .get(`/cases/summary?case_id=${caseId}`)
      // `data` and `loading` are set together so React 18 batches them into a
      // single commit (two commits make the panel flicker, and make every test
      // that waits on it race the second update).
      .then((res: any) => {
        if (!alive.current) return;
        setData((res && res.data) || { summary: null, generated_at: null, error: "unavailable" });
        setLoading(false);
      })
      .catch(() => {
        if (!alive.current) return;
        setData({ summary: null, generated_at: null, error: "unavailable" });
        setLoading(false);
      });
  }, [caseId]);

  useEffect(() => {
    load();
    // activityCount is part of the cache key on the server side.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, activityCount]);

  const regenerate = () => {
    if (regenerating) return;
    setRegenerating(true);
    axiosInstance
      .post(`/cases/summary/regenerate`, { case_id: caseId })
      .then((res: any) => {
        if (!alive.current) return;
        if (res && res.data) setData(res.data);
      })
      .catch(() => {
        if (!alive.current) return;
        setData((current) =>
          current && current.summary
            ? current
            : { summary: null, generated_at: null, error: "unavailable" },
        );
      })
      .finally(() => {
        if (alive.current) setRegenerating(false);
      });
  };

  const summary = ((data && data.summary) || "").trim();
  const generatedAt =
    data && data.generated_at ? new Date(data.generated_at) : null;
  const generatedLabel =
    generatedAt && !Number.isNaN(generatedAt.getTime())
      ? `Generated ${formatRelative(generatedAt)}`
      : "Generated";

  /** The body of the panel — one of: loading line, text, retry line, nothing. */
  let body: React.ReactNode = null;
  if (loading && !data) {
    body = (
      <Box
        aria-label="Loading case summary"
        sx={{ fontSize: "13px", lineHeight: 1.5, color: FAINT }}
      >
        Generating summary…
      </Box>
    );
  } else if (summary) {
    body = (
      <Box
        sx={{
          fontSize: "13px",
          lineHeight: 1.55,
          color: INK_2,
          whiteSpace: "pre-wrap",
        }}
      >
        {summary}
      </Box>
    );
  } else if (data && data.error === "unavailable") {
    body = (
      <Box sx={{ fontSize: "13px", lineHeight: 1.5, color: FAINT }}>
        Summary unavailable right now ·{" "}
        <Box component="button" type="button" onClick={load} sx={linkSx} disabled={loading}>
          Try again
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: PANEL_BG,
        border: `1px solid ${RULE}`,
        borderRadius: "4px",
        padding: "9px 11px",
      }}
    >
      <Box
        component="span"
        sx={{
          display: "block",
          fontFamily: MONO,
          fontSize: "11px",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: FAINT,
          marginBottom: "5px",
        }}
      >
        Summary
      </Box>
      {body}
      {footer || summary ? (
        <Box sx={footerSx}>
          {footer}
          {footer && summary ? <Box sx={{ flexGrow: 1 }} /> : null}
          {summary ? (
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
              <span>{generatedLabel}</span>
              <span aria-hidden="true">·</span>
              {regenerating ? (
                <span>Regenerating…</span>
              ) : (
                <Box component="button" type="button" onClick={regenerate} sx={linkSx}>
                  Regenerate
                </Box>
              )}
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

export default CaseSummaryCard;
